import { i } from "@instantdb/react";

type PushSubscription = {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
};

export type WhatsappConnectionEvent =
  | "autocloseCalled"
  | "autocloseCalled"
  | "browserClose"
  | "browserClose"
  | "clientCreated"
  | "disconnectedMobile"
  | "disconnectedMobile"
  | "dockerStarted"
  | "inChat"
  | "isLogged"
  | "notLogged"
  | "pairingCodeIssued"
  | "pairingCodeRateLimited"
  | "performedWarmup"
  | "phoneNotConnected"
  | "phoneNotConnected"
  | "pod is down"
  | "provisioningServer"
  | "qrReadError"
  | "qrReadError"
  | "qrReadFail"
  | "qrReadFail"
  | "qrReadSuccess"
  | "serverClose"
  | "serverClose"
  | "serverProvisioned"
  | "startingDocker"
  | "streamMode:CONFLICT"
  | "streamMode:DEPRECATED_VERSION"
  | "streamMode:MAIN"
  | "streamMode:OFFLINE"
  | "streamMode:PROXYBLOCK"
  | "streamMode:QR"
  | "streamMode:SMB_TOS_BLOCK"
  | "streamMode:SYNCING"
  | "streamMode:TOS_BLOCK"
  | "autoConnectDisabled"
  | "manualReconnectRequested"
  | "noProxyAssigned"
  | "qrLoopStopped"
  | "phoneNumberMismatch"
  | `phoneNumberMismatch:${string}`
  | "waitingForSsh"
  | "dailyMessageLimitReached"
  | `qrAttempt:${number}`
  | `streamMode:${string}`;

export const whatsappAccountStatus = {
  connected: "inChat" as const,
  podIsDown: "pod is down" as const,
  startingDocker: "startingDocker" as const,
  dockerStarted: "dockerStarted" as const,
  provisioningServer: "provisioningServer" as const,
  waitingForSsh: "waitingForSsh" as const,
  serverProvisioned: "serverProvisioned" as const,
};

const _schema = i.schema({
  entities: {
    $files: i.entity({
      path: i.string().unique().indexed(),
      url: i.any(),
    }),
    $users: i.entity({
      email: i.string().unique().indexed(),
    }),
    users: i.entity({
      email: i.string().unique().indexed(),
      pushSubscriptions: i.json<Record<string, PushSubscription>>(),
      lastSeen: i.number().indexed(),
      whatsapp: i.string().optional().indexed(),
      monthlyPayment: i.number().optional().indexed(),
      paidCapacity: i.number().optional().indexed(),
      createdAt: i.number().indexed(),
      trialWarningLastSentAt: i.number().optional().indexed(),
      stripeCustomerId: i.string().optional().indexed(),
      personalPhone: i.string().optional().unique().indexed(),
      phoneVerifiedAt: i.number().optional().indexed(),
    }),
    whatsappUsers: i.entity(
      { whatsappId: i.string().unique().indexed() },
    ),
    groups: i.entity({
      whatsappId: i.string().unique().indexed(),
      name: i.string().indexed().optional().indexed(),
    }),
    chats: i.entity({
      chatId: i.string().indexed().unique(),
      network: i.string().indexed(),
    }),
    webhooks: i.entity({
      name: i.string().indexed().unique(),
      url: i.string().indexed(),
    }),
    whatsappAccounts: i.entity({
      name: i.string().indexed(),
      phoneNumber: i.string().indexed().unique(),
      webhook: i.string().indexed().optional(),
      notes: i.string().optional().indexed(),
      port: i.number().indexed().optional(),
      statusHistory: i.json<
        Array<{ status: WhatsappConnectionEvent; timestamp: number }>
      >(),
      lastEventTime: i.number().indexed().optional(),
      verificationCode: i.string().optional().unique().indexed(),
      qrIssuedAt: i.number().optional().indexed(),
      pairingCode: i.string().optional().indexed(),
      pairingCodeIssuedAt: i.number().optional().indexed(),
      isConnected: i.boolean().indexed(),
      firstConnectedAt: i.number().optional().indexed(),
      disconnectedAt: i.number().optional().indexed(),
      disconnectNotifiedAt: i.number().optional().indexed(),
      devDisconnectNotifiedAt: i.number().optional().indexed(),
      secretToken: i.string().indexed().unique(),
      dockerImage: i.string().optional().indexed(),
      isEnabled: i.boolean().optional().indexed(),
      disabledAt: i.number().optional().indexed(),
      webhookUrls: i.json<string[]>().optional(),
      preferCodeLogin: i.boolean().optional(),
      nextWarmupAt: i.number().optional().indexed(),
      avatarUrl: i.string().optional(),
      bannedAt: i.number().optional().indexed(),
    }),
    phoneNumbers: i.entity({
      number: i.string().indexed().unique(),
      createdAt: i.number().indexed(),
      provider: i.string().indexed().optional(),
      providerDidId: i.string().indexed().optional(),
      stripeSubscriptionId: i.string().indexed().optional(),
      status: i.string().indexed().optional(),
      releasedAt: i.number().indexed().optional(),
      price: i.number().indexed().optional(),
    }),
    smsMessages: i.entity({
      providerMessageId: i.string().indexed().optional(),
      did: i.string().indexed(),
      fromNumber: i.string().indexed(),
      toNumber: i.string().indexed().optional(),
      body: i.string(),
      receivedAt: i.number().indexed(),
      rawPayload: i.json<Record<string, unknown>>().optional(),
    }),
    staticProxies: i.entity({
      oxylabsPort: i.number().indexed().unique().optional(),
      country: i.string().indexed().optional(),
      provider: i.string().indexed().optional(),
      sessionId: i.string().indexed().optional(),
    }),
    fbAccounts: i.entity({
      status: i.string().indexed().optional(),
      stripeSubscriptionId: i.string().indexed().optional(),
      webhookUrls: i.json<string[]>().optional(),
      kernelProfileName: i.string().indexed().optional(),
      secretToken: i.string().indexed().optional(),
      createdAt: i.number().indexed(),
      releasedAt: i.number().indexed().optional(),
      isEnabled: i.boolean().optional().indexed(),
      nextScrapeAt: i.number().indexed().optional(),
      consecutiveLoginFailures: i.number().indexed().optional(),
      lastEventTime: i.number().indexed().optional(),
    }),
    fbGroups: i.entity({
      groupId: i.string().indexed().unique(),
      name: i.string().indexed().optional(),
      lastScrapedAt: i.number().indexed().optional(),
      lastEventTime: i.number().indexed().optional(),
    }),
    fbPosts: i.entity({
      postId: i.string().indexed().unique(),
      authorName: i.string().indexed().optional(),
      body: i.string(),
      postedAt: i.number().indexed(),
      rawPayload: i.json<Record<string, unknown>>().optional(),
    }),
    telegramAccounts: i.entity({
      phoneNumber: i.string().indexed().unique(),
      name: i.string().indexed(),
      apiId: i.number().indexed(),
      apiHash: i.string().indexed(),
      sessionString: i.string().optional().indexed(),
      webhook: i.string().indexed().optional(),
      webhookUrls: i.json<string[]>().optional(),
      isConnected: i.boolean().indexed(),
      isEnabled: i.boolean().optional().indexed(),
      disabledAt: i.number().optional().indexed(),
      secretToken: i.string().indexed().unique(),
      statusHistory: i.json<
        Array<{ status: string; timestamp: number }>
      >().optional(),
      lastEventTime: i.number().indexed().optional(),
    }),
    redditAccounts: i.entity({
      name: i.string().indexed().optional(),
      status: i.string().indexed().optional(),
      kernelProfileName: i.string().indexed().optional(),
      secretToken: i.string().indexed().optional(),
      isConnected: i.boolean().indexed().optional(),
      isEnabled: i.boolean().optional().indexed(),
      disabledAt: i.number().optional().indexed(),
      createdAt: i.number().indexed(),
      webhookUrls: i.json<string[]>().optional(),
      lastEventTime: i.number().indexed().optional(),
    }),
    linkedInAccounts: i.entity({
      name: i.string().indexed().optional(),
      status: i.string().indexed().optional(),
      kernelProfileName: i.string().indexed().optional(),
      secretToken: i.string().indexed().optional(),
      isConnected: i.boolean().indexed().optional(),
      isEnabled: i.boolean().optional().indexed(),
      disabledAt: i.number().optional().indexed(),
      createdAt: i.number().indexed(),
      releasedAt: i.number().indexed().optional(),
      consecutiveLoginFailures: i.number().indexed().optional(),
      lastEventTime: i.number().indexed().optional(),
    }),
    servers: i.entity({
      ip: i.string().unique().indexed(),
      providerId: i.string().unique().indexed(),
      name: i.string().indexed(),
      provider: i.string().indexed(),
      capacity: i.number().indexed().optional(),
    }),
  },
  links: {
    whatsappAccountServer: {
      forward: {
        on: "whatsappAccounts",
        has: "one",
        label: "server",
      },
      reverse: {
        on: "servers",
        has: "many",
        label: "whatsappAccounts",
      },
    },
    whatsappAccountStaticProxies: {
      forward: {
        on: "whatsappAccounts",
        has: "one",
        label: "staticProxy",
      },
      reverse: {
        on: "staticProxies",
        has: "many",
        label: "whatsappAccount",
      },
    },
    whatsappAccountsAdmins: {
      forward: {
        on: "whatsappAccounts",
        has: "many",
        label: "admins",
        required: true,
      },
      reverse: { on: "users", has: "many", label: "whatsappAccounts" },
    },
    phoneNumbersAdmins: {
      forward: {
        on: "phoneNumbers",
        has: "many",
        label: "admins",
        required: true,
      },
      reverse: { on: "users", has: "many", label: "adminPhoneNumbers" },
    },
    phoneNumberBillingUser: {
      forward: {
        on: "phoneNumbers",
        has: "one",
        label: "billingUser",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "billingPhoneNumbers",
      },
    },
    whatsappAccountBillingUser: {
      forward: {
        on: "whatsappAccounts",
        has: "one",
        label: "billingUser",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "billingAccounts",
      },
    },
    chatWebhooks: {
      forward: { on: "chats", label: "webhooks", has: "many" },
      reverse: { on: "webhooks", label: "chats", has: "many" },
    },
    usersGroups: {
      forward: { on: "users", has: "many", label: "groups" },
      reverse: { on: "groups", has: "many", label: "members" },
    },
    fbAccountAdmins: {
      forward: {
        on: "fbAccounts",
        has: "many",
        label: "admins",
        required: true,
      },
      reverse: { on: "users", has: "many", label: "fbAccounts" },
    },
    fbAccountBillingUser: {
      forward: {
        on: "fbAccounts",
        has: "one",
        label: "billingUser",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "billingFbAccounts",
      },
    },
    fbAccountGroups: {
      forward: { on: "fbAccounts", has: "many", label: "fbGroups" },
      reverse: { on: "fbGroups", has: "one", label: "fbAccount" },
    },
    fbGroupPosts: {
      forward: { on: "fbGroups", has: "many", label: "fbPosts" },
      reverse: { on: "fbPosts", has: "one", label: "fbGroup" },
    },
    phoneNumberSmsMessages: {
      forward: { on: "smsMessages", has: "one", label: "phoneNumber" },
      reverse: { on: "phoneNumbers", has: "many", label: "smsMessages" },
    },
    telegramAccountAdmins: {
      forward: {
        on: "telegramAccounts",
        has: "many",
        label: "admins",
        required: true,
      },
      reverse: { on: "users", has: "many", label: "telegramAccounts" },
    },
    telegramAccountBillingUser: {
      forward: {
        on: "telegramAccounts",
        has: "one",
        label: "billingUser",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "billingTelegramAccounts",
      },
    },
    redditAccountAdmins: {
      forward: {
        on: "redditAccounts",
        has: "many",
        label: "admins",
        required: true,
      },
      reverse: { on: "users", has: "many", label: "redditAccounts" },
    },
    redditAccountBillingUser: {
      forward: {
        on: "redditAccounts",
        has: "one",
        label: "billingUser",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "billingRedditAccounts",
      },
    },
    linkedInAccountAdmins: {
      forward: {
        on: "linkedInAccounts",
        has: "many",
        label: "admins",
        required: true,
      },
      reverse: { on: "users", has: "many", label: "linkedInAccounts" },
    },
    linkedInAccountBillingUser: {
      forward: {
        on: "linkedInAccounts",
        has: "one",
        label: "billingUser",
      },
      reverse: {
        on: "users",
        has: "many",
        label: "billingLinkedInAccounts",
      },
    },
  },
  rooms: {},
});

type _AppSchema = typeof _schema;
interface AppSchema extends _AppSchema {}
const schema: AppSchema = _schema;

export type { AppSchema };
export default schema;
